import { createContext, useCallback, useContext, useLayoutEffect, useReducer, useRef, ReactNode } from "react";
import { useDomain } from "../context/DomainContext";
import { saveThemePreference } from "./fetchBranding";
import {
  currentThemeId,
  documentFor,
  initialThemeState,
  themeReducer,
  type ThemeSummary,
} from "./pickTheme";
import { persistLocalPref, prefKey, THEME_READY_CLASS } from "./themeCache";
import contractJson from "./theme-contract.json";
import { commitPresentation } from "./themeVariables";
import type { ThemeContract } from "./resolveTheme";

const contract = contractJson as ThemeContract;

interface ThemeContextValue {
  themes: ThemeSummary[];
  currentThemeId: string;
  brandThemeId: string | null;
  setTheme: (id: string | null, options?: { save?: boolean }) => void;
  signOutTheme: () => void;
  applyThemePreference: (id: string | null | undefined) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readLocalPref(): string | null {
  try {
    return localStorage.getItem(prefKey(window.location.origin));
  } catch {
    // Private mode can throw. Selection then uses the brand default.
    return null;
  }
}

function browserTarget() {
  let meta = document.querySelector<HTMLMetaElement>("meta[name=\"theme-color\"]");
  return {
    root: document.documentElement,
    origin: window.location.origin,
    storage: localStorage,
    setThemeColor(color: string) {
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "theme-color";
        document.head.appendChild(meta);
      }
      meta.content = color;
    },
    appliedKeys: [] as string[],
  };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { brands, themes: documents, domainMatchedBrand, isLoading, brandingOk } = useDomain();
  const target = useRef(browserTarget());
  const tickets = useRef(1);
  const [state, dispatch] = useReducer(themeReducer, undefined, () => initialThemeState(readLocalPref()));

  const issue = () => {
    const ticket = tickets.current;
    tickets.current += 1;
    return ticket;
  };

  useLayoutEffect(() => {
    if (isLoading) return;
    if (!brandingOk) {
      dispatch({ type: "BRANDING_FAILED" });
      return;
    }
    dispatch({
      type: "BRANDING_LOADED",
      brands,
      themes: documents,
      contract,
      domainBrand: domainMatchedBrand,
    });
  }, [isLoading, brandingOk, brands, documents, domainMatchedBrand]);

  useLayoutEffect(() => {
    if (isLoading || state.brandingStatus === "pending") return;
    const id = currentThemeId(state);
    try {
      commitPresentation(target.current, documentFor(state), contract, id);
    } catch (error) {
      console.warn("theme apply failed", error);
      document.documentElement.classList.add(THEME_READY_CLASS);
    }
    if (state.prefWrite === 0) return;
    try {
      persistLocalPref(localStorage, prefKey(window.location.origin), state.localPref);
    } catch {
      // The server copy is still updated when a save was requested.
    }
  }, [state, isLoading]);

  useLayoutEffect(() => {
    const request = state.saveRequest;
    if (!request) return;
    const userId = sessionStorage.getItem("currentUserId");
    const env = sessionStorage.getItem("currentEnvironment") || "staging";
    if (!userId) return;
    void saveThemePreference(fetch, userId, env, request.id).then((ok) => {
      if (!ok) console.warn("Could not save the theme preference");
    });
  }, [state.saveRequest]);

  // Stable: the mapping effect must not re-run when brands arrive.
  const applyThemePreference = useCallback((id: string | null | undefined) => {
    dispatch({ type: "SERVER_PREFERENCE", id: id ?? null, ticket: issue() });
  }, []);

  const setTheme = useCallback((id: string | null, options?: { save?: boolean }) => {
    dispatch({ type: "USER_SELECTED", id, ticket: issue(), save: options?.save !== false });
  }, []);

  const signOutTheme = useCallback(() => {
    try {
      persistLocalPref(localStorage, prefKey(window.location.origin), null);
    } catch {
      // Private mode can throw. The reducer still clears the in-memory preference.
    }
    dispatch({ type: "SIGNED_OUT", ticket: issue() });
  }, []);

  const value: ThemeContextValue = {
    themes: state.summaries,
    currentThemeId: currentThemeId(state),
    brandThemeId: state.brandThemeId,
    setTheme,
    signOutTheme,
    applyThemePreference,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
